import "react-toastify/dist/ReactToastify.css";
import { ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import { IPurchasablePaginatedResponse } from "@smartrr/shared/entities/Purchasable";
import { AppReqResponse } from "@smartrr/shared/req";
import { IShopifyLiquidCustomer } from "@smartrr/shared/shopifyLiquid/customer";
import {
  ICustomerPortalValidationObj,
  ICustomerPortalWithValidation,
} from "@smartrr/shared/shopifyLiquid/validation";
import { captureException } from "@smartrr/shared/utils/captureException";
import { delay } from "@smartrr/shared/utils/delay";
import { MAX_PAGE_SIZE, serializePaginatedQueryValues } from "@smartrr/shared/utils/paginatedQuery";
import flatten from 'lodash/flatten';
import React from "react";
import { createRoot } from "react-dom/client";

import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { loadAddresses } from "@customer-app/state/actionCreators/addresses";
import {
  loadCustomer,
  setLiquidCustomer,
  setLiquidValidation,
  upsertAndLoadCustomer,
} from "@customer-app/state/actionCreators/auth";
import { loadBundles } from "@customer-app/state/actionCreators/bundles";
import { loadAllConfigs, loadSubscriptions } from "@customer-app/state/actionCreators/customerPurchaseStates";
import {
  loadCancellationReasons,
  loadOrgAccountPlan,
  loadPauseReasons,
} from "@customer-app/state/actionCreators/retention";
import { loadTheme, setTheme } from "@customer-app/state/actionCreators/theme";
import { getMockValidationAndCustomerLiquid } from "@customer-app/state/actionCreators/viewAsCustomer";
import { store } from "@customer-app/state/store";

import App from "./App";
import { extensionsConfig } from "@customer-app/utils/extensionsConfig";

const windowAny = window as any;
const CHUNK_SIZE = 5;
const DELAY_TIME = 1000;

if (!windowAny.smartrr) {
  windowAny.smartrr = {};
}
windowAny.smartrr.account = {
  initialize,
};

const theme: ICustomerPortalTheme | undefined = extensionsConfig?.theme ?? windowAny.smartrr.theme;

async function initialize({
  validation,
  liquidCustomer,
}: {
  liquidCustomer?: IShopifyLiquidCustomer;
  validation: ICustomerPortalValidationObj;
}) {
  // Validation from original liquid customer needs to be set first before mock customer's validation
  if (validation) {
    store.dispatch(setLiquidValidation(validation));
  }

  liquidCustomer = await checkUrlParams(liquidCustomer, validation);

  if (liquidCustomer) {
    store.dispatch(setLiquidCustomer(liquidCustomer));
  }

  if (theme) {
    store.dispatch(setTheme(theme));
  } else {
    await store.dispatch(loadTheme());
  }

  const rootElement = document.getElementById("smartrr_account");
  if (rootElement) {
    const root = createRoot(rootElement);

    // ensure one tick passes before render in case redux-thunk forces this
    setTimeout(async () => {
      if (liquidCustomer) {
        await store.dispatch(upsertAndLoadCustomer(liquidCustomer));
      } else {
        await store.dispatch(loadCustomer());
      }

      const [getMorePurchasables] = await Promise.all([
        fetchPurchasables(),
        loadBundles(),
        store.dispatch(loadAddresses()),
        store.dispatch(loadSubscriptions()),
        loadAllConfigs(true),
      ]);

      //load cancellation and pause reasons only when retention is available
      await store.dispatch(loadOrgAccountPlan()).then(async action => {
        if (action.type === "LOADED_ORGANIZATION_PLAN" && action.payload.accountPlan.features.RETENTION) {
          await Promise.all([store.dispatch(loadCancellationReasons()), store.dispatch(loadPauseReasons())]);
        }
      });

      getMorePurchasables?.();
    }, 0);
    root.render(<App />);
  }
}

const fetchPurchasables = async () => {
  // fetch just one page to get totalCount and totalPages
  try {
    const res = await typedCustomerFrontendApi.getReq("/purchasable", {
      query: serializePaginatedQueryValues({
        pageSize: MAX_PAGE_SIZE,
        pageNumber: 0,
        filterLike: { includeDeleted: "true" },
      }),
    });
    if (res.type === "success" && res.body) {
      const { totalPages, totalCount, data } = res.body;
      const purchasables: IPurchasablePaginatedResponse = {
        data,
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        totalCount,
        totalPages,
      };

      if (totalPages === 1) {
        store.dispatch({
          type: "LOADED_COMPLETE_PURCHASABLES",
          payload: {
            purchasables,
          },
        });
      } else {
        store.dispatch({
          type: "LOADED_PURCHASABLES",
          payload: {
            purchasables,
          },
        });

        return async () => {
          try {
            const purchasablePromises: AppReqResponse<IPurchasablePaginatedResponse>[][] = [];
            for (
              let chunkIndex = 0;
              chunkIndex < Math.ceil(totalPages - MAX_PAGE_SIZE / MAX_PAGE_SIZE);
              chunkIndex += CHUNK_SIZE
            ) {
              purchasablePromises.push(
                await Promise.all(
                  //this piece generates and populates array from 0 to N
                  //removing the first page batch from total and starting from pagenumber === 1
                  //to exclude the first page that was fetched separately
                  [
                    ...new Array(
                      totalPages - chunkIndex > CHUNK_SIZE ? CHUNK_SIZE : totalPages - chunkIndex
                    ).keys(),
                  ].map((item, index) => {
                    return typedCustomerFrontendApi.getReq("/purchasable", {
                      query: serializePaginatedQueryValues({
                        pageSize: MAX_PAGE_SIZE,
                        //index + 1 is needed to exclude the first page
                        pageNumber: chunkIndex === 0 ? chunkIndex + index + 1 : chunkIndex + index,
                      }),
                    });
                  })
                )
              );
              await delay(DELAY_TIME);
            }

            //go through the promises to store data in the purchasables variable
            flatten(purchasablePromises).map((promise, index) => {
              if (promise.type === "success") {
                //reassign default purchasables values with the ones from the request
                //only changing the data property, everything else in the request is the same
                purchasables.data = [...purchasables.data, ...promise.body.data];
              }
            });

            //update the redux state with all the data once
            store.dispatch({
              type: "LOADED_ADDITIONAL_PURCHASABLES",
              payload: {
                purchasables,
              },
            });
          } catch (error) {
            captureException("Failed to fetch rest of purchasables", error);
          }
        };
      }
    } else {
      // if response is returned with a status other than success, throw response
      throw res;
    }
  } catch (error) {
    captureException("Failed to fetch purchasables", error);
  }
};

async function checkUrlParams(
  liquidCustomer: IShopifyLiquidCustomer | undefined,
  validation: ICustomerPortalValidationObj
) {
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams) {
    return liquidCustomer;
  }
  const shopifyId = urlParams.get("id");
  const sig = urlParams.get("sig");
  if (!shopifyId || !sig || !liquidCustomer) {
    return liquidCustomer;
  }
  try {
    const res = await getMockValidationAndCustomerLiquid(
      shopifyId,
      liquidCustomer,
      sig,
      (validation as ICustomerPortalWithValidation).exp
    );
    if (res.type === "success") {
      store.dispatch(setLiquidValidation(res.body.mockValidation));
      liquidCustomer = res.body.mockLiquidCustomer;
    }
  } catch (error) {
    captureException("Failed to validate url params", error);
  }
  return liquidCustomer;
}
